import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Download } from 'lucide-react';

interface ImageAttachment {
  id: string;
  name: string;
  signedUrl: string;
}

interface ImageViewerModalProps {
  images: ImageAttachment[];
  initialIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownload?: (attachment: ImageAttachment) => void;
}

export function ImageViewerModal({ 
  images, 
  initialIndex, 
  open, 
  onOpenChange,
  onDownload 
}: ImageViewerModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;
      
      switch (event.key) {
        case 'Escape':
          onOpenChange(false);
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, currentIndex, images.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setImageLoading(true);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setImageLoading(true);
  };

  const currentImage = images[currentIndex];
  const hasMultipleImages = images.length > 1;

  if (!currentImage) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
        <div className="relative bg-black/95 flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-background/10 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {currentImage.name}
              </span>
              {hasMultipleImages && (
                <span className="text-xs text-muted-foreground">
                  ({currentIndex + 1} de {images.length})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {onDownload && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDownload(currentImage)}
                  className="text-white hover:bg-white/10"
                >
                  <Download className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost" 
                size="sm"
                onClick={() => onOpenChange(false)}
                className="text-white hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Image Container */}
          <div className="flex-1 flex items-center justify-center p-4 relative">
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            )}
            
            <img
              src={currentImage.signedUrl}
              alt={currentImage.name}
              className="max-w-full max-h-full object-contain"
              onLoad={() => setImageLoading(false)}
              onError={() => setImageLoading(false)}
            />

            {/* Navigation Buttons */}
            {hasMultipleImages && (
              <>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={handlePrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 h-12 w-12 rounded-full"
                  disabled={imageLoading}
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={handleNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 h-12 w-12 rounded-full"
                  disabled={imageLoading}
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}
          </div>

          {/* Footer with thumbnails for multiple images */}
          {hasMultipleImages && (
            <div className="p-4 bg-background/10 backdrop-blur-sm">
              <div className="flex justify-center gap-2 overflow-x-auto">
                {images.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => {
                      setCurrentIndex(index);
                      setImageLoading(true);
                    }}
                    className={`flex-shrink-0 w-12 h-12 rounded border-2 overflow-hidden ${
                      index === currentIndex 
                        ? 'border-primary' 
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <img
                      src={image.signedUrl}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}